import http from "helpers/http.js";
import Analytics from "analytics";

const API_URL = process.env.REACT_APP_API_URL;

const register = (email, password, country) => {
    var data = JSON.stringify({
        "Email": email,
        "Password": password,
        "Country": country,
    });

    var config = {
        method: 'post',
        url: API_URL + 'api/Authenticate/register-no-voucher',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(config)
        .then(function (response) {
            console.log(JSON.stringify(response.data));
            const status = response.status;

            Analytics.signUp();
            return response;
        })
        .catch(function (error) {
            console.log(error);
            return error.response.data;
        });
};

/**
 * Logs in a user using email/password or an external provider.
 *
 * @param {Object} req - The login request object.
 * @param {string} [req.provider] - The external provider name (e.g., "google"). If not provided, email/password login is used.
 * @param {string} [req.email] - The user's email address (required for email/password login).
 * @param {string} [req.password] - The user's password (required for email/password login).
 * @param {string} [req.idToken] - The external provider ID token (required for external login).
 * @param {string} [req.QRSessionID] - Optional QR session ID for device login.
 * @param {string} [req.country] - Optional country code.
 * @returns {Promise<Object>} Resolves with user data on success, or error data on failure.
 */
const login = (req) => {
    var data;
    var configLogin;
    if (!req.provider || req.provider === "none") {
        data = JSON.stringify({
            Email: req.email,
            Password: req.password,
            QRSessionID: req.QRSessionID,
            Country: req.country,
            ForceLogin: req.forceLogin || false,
            SessionType: req.sessionType,
        });

        configLogin = {
            method: 'post',
            url: API_URL + 'api/Authenticate/login',
            headers: {
                'Content-Type': 'application/json'
            },
            data: data
        };
    } else {
        data = JSON.stringify({
            provider: req.provider,
            IdToken: req.idToken,
            code: req.code,
            QRSessionID: req.QRSessionID,
            Country: req.country,
            ForceLogin: req.forceLogin || false,
            SessionType: req.sessionType,
        });

        configLogin = {
            method: 'post',
            url: API_URL + 'api/Authenticate/external-login',
            headers: {
                'Content-Type': 'application/json'
            },
            data: data
        };
    }

    return http(configLogin)
        .then(function (response) {
            console.log(response)
            if (response.data.token) {
                localStorage.setItem("user", JSON.stringify(response.data));
            }
            //localStorage.setItem("games", JSON.stringify(response.data.games));
            return response.data;

        }).catch(function (error) {
            console.log(error);
            // 409 from the login endpoints is always SessionConflict on this API.
            // Normalise the body to lowercase keys + status="SessionConflict" so the
            // action layer can dispatch the modal regardless of how the response
            // is cased on the wire (e.g. PascalCase via a proxy that bypasses the
            // server's camelCase contract resolver).
            if (error.response && error.response.status === 409) {
                const data = error.response.data || {};
                return {
                    ...data,
                    status: "SessionConflict",
                    message: data.message ?? data.Message,
                    activeSessions: data.activeSessions ?? data.ActiveSessions,
                    sessionType: data.sessionType ?? data.SessionType,
                };
            }
            return error.response?.data;
        });
};

const checkAccountExists = (email) => {
    var data = JSON.stringify({
        "Username": email
    })

    var request = {
        method: 'post',
        url: API_URL + 'api/Authenticate/check-account-exists',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(request)
        .then(function (response) {
            return response.data;
        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
}

/// <summary>
/// Requests a QR session for device login.
/// </summary>
/// <param name="oldSessionID">The old session ID to overwrite (optional).</param>
/// <param name="ttl">The time-to-live (TTL) for the session in seconds.</param>
/// <returns>A promise that resolves with the session data.</returns>
/// <remarks>
const requestQRSession = (oldSessionID, ttl, originSessionType) => {
    var data = JSON.stringify({
        "OverwriteSessionID": oldSessionID,
        "ttl": ttl,
        "OriginSessionType": originSessionType,
    });

    var request = {
        method: 'post',
        url: API_URL + 'api/Authenticate/request-qr-session',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(request)
        .then(function (response) {
            if (response.data?.sessionID) {
                localStorage.setItem("QRSessionData", JSON.stringify(response.data));
            }
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
}

const getQRSession = (QRSessionID) => {
    var data = JSON.stringify({
        "QRSessionID": QRSessionID,
    });

    var request = {
        method: 'post',
        url: API_URL + 'api/Authenticate/get-qr-session',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(request)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return { ...error.response.data, error };
        });
}

const autoLoginQRSession = (QRSessionID, UserID, originSessionType, forceLogin = false) => {
    var data = JSON.stringify({
        "QRSessionID": QRSessionID,
        "UserID": UserID,
        "OriginSessionType": originSessionType,
        "ForceLogin": forceLogin,
    });

    var request = {
        method: 'post',
        url: API_URL + 'api/Authenticate/auto-login-qr-session',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(request)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            // 409 from this endpoint is always SessionConflict — normalise to lowercase
            // keys + status="SessionConflict" so the action dispatch is casing-proof.
            if (error.response && error.response.status === 409) {
                const data = error.response.data || {};
                return {
                    ...data,
                    status: "SessionConflict",
                    message: data.message ?? data.Message,
                    activeSessions: data.activeSessions ?? data.ActiveSessions,
                    sessionType: data.sessionType ?? data.SessionType,
                };
            }
            return error?.response?.data ? { ...error.response.data, error } : { error };
        });
}


const getLatestRoomID = (QRSessionID) => {
    var data = JSON.stringify({
        "QRSessionID": QRSessionID,
    });

    var request = {
        method: 'post',
        url: API_URL + 'api/Authenticate/get-roku-room-id',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(request)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return { ...error.response.data, error };
        });
}

const logout = () => {
    // Fire-and-forget server-side logout to end the session
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user && user.token) {
            http({
                method: 'post',
                url: API_URL + 'api/Authenticate/logout',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + user.token,
                },
            }).catch(() => {}); // Ignore errors - fire and forget
        }
    } catch (e) {
        // Ignore errors during logout
    }
    localStorage.removeItem("user");
    localStorage.removeItem("games");
};

const getGames = (email) => {
    var configGetGames = {
        method: 'post',
        url: API_URL + 'api/Authenticate/GetGames',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            email,
        }
    };

    return http(configGetGames)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
};

const redeemCode = (code) => {
    var configGetGames = {
        method: 'post',
        url: API_URL + 'api/Authenticate/ApplyVoucherCode',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            voucherCode: code,
        }
    };

    return http(configGetGames)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
}

const checkCode = (code) => {
    var configGetGames = {
        method: 'post',
        url: API_URL + 'api/Authenticate/CheckVoucherCode',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            voucherCode: code,
        }
    };

    return http(configGetGames)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
}

const redeemMultipleCodes = (codes) => {
    var configGetGames = {
        method: 'post',
        url: API_URL + 'api/Authenticate/ApplyMultipleVoucherCodes',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            voucherCodes: codes,
        }
    };

    return http(configGetGames)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
}

const sendGiftVoucher = (codes) => {
    var configSendGiftVoucher = {
        method: 'post',
        url: API_URL + 'api/Authenticate/SendGiftVoucher',
        headers: {
            'Content-Type': 'application/json'
        },
        data: {
            VoucherCodes: codes,
        }
    };

    return http(configSendGiftVoucher)
        .then(function (response) {
            return response.data;

        }).catch(function (error) {
            console.log(error);
            return error.response.data;
        });
}

const forgotPassword = (email) => {
    var data = JSON.stringify({
        "Email": email,
    });

    var configForgotPassword = {
        method: 'post',
        url: API_URL + 'api/Authenticate/forgot-password',
        headers: {
            'Content-Type': 'application/json'
        },
        data,
    };

    return http(configForgotPassword)
        .then((response) => {
            return response.data;
        }).catch((error) => {
            console.log(error);
            return error.response.data;
        });
}

const resetPassword = (email, token, password, confirmPassword) => {
    var data = JSON.stringify({
        "Email": email,
        "Token": token,
        "Password": password,
        "ConfirmPassword": confirmPassword
    });

    var configReset = {
        method: 'post',
        url: API_URL + 'api/Authenticate/reset-password',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(configReset)
        .then((response) => {
            return response.data;

        }).catch((error) => {
            console.log(error);
            return error.response.data;
        });
}

const resetPIN = (pin, confirmPin) => {
    var data = JSON.stringify({
        "PIN": pin,
        "ConfirmPIN": confirmPin
    });

    var configReset = {
        method: 'post',
        url: API_URL + 'api/Authenticate/reset-pin',
        headers: {
            'Content-Type': 'application/json'
        },
        data: data
    };

    return http(configReset)
        .then((response) => {
            return response.data;

        }).catch((error) => {
            console.log(error);
            return error.response.data;
        });
}


const checkSession = () => {
    return http({ method: 'get', url: API_URL + 'api/Authenticate/session-check' });
};

// Mints a short-lived single-use code for the Con Artist "On the Go" login handover.
// Requires a logged-in user — the http helper attaches the Bearer token.
// Resolves with { code, expiresInSeconds } or rejects on failure (caller falls back
// to opening conartist.io without a code).
const createHandoverCode = () => {
    return http({
        method: 'post',
        url: API_URL + 'api/Authenticate/create-handover-code',
        headers: {
            'Content-Type': 'application/json'
        },
    }).then((response) => response.data);
};

export default {
    register,
    login,
    logout,
    getGames,
    redeemCode,
    redeemMultipleCodes,
    forgotPassword,
    resetPassword,
    resetPIN,
    checkCode,
    requestQRSession,
    getQRSession,
    autoLoginQRSession,
    getLatestRoomID,
    sendGiftVoucher,
    checkAccountExists,
    checkSession,
    createHandoverCode
};
